/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: HotDeskingPage.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Hot Desking list page - simplified using hooks and API modules.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Edit, Copy, Laptop, Code, X } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useDebugMode } from '../../hooks/useDebugMode';
import { useContextStore } from '../../store/context';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import { deleteHotDeskById, navigateToHotDeskDuplicate } from './hotDeskingActions';
import { useHotDeskingList } from './hooks/useHotDeskingList';
import { fetchHotDeskingDeviceXml } from './api/hotDeskingApi';
import type { HotDeskDevice, HotDeskingColumn } from './types';

export default function HotDeskingPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { isActive: isDebugModeActive } = useDebugMode();
  const { selectedDomain } = useContextStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<HotDeskDevice | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [xmlModal, setXmlModal] = useState<{ isOpen: boolean; device: HotDeskDevice | null; xml: string }>({
    isOpen: false,
    device: null,
    xml: '',
  });

  const availableColumns = useMemo<HotDeskingColumn[]>(() => [
    { key: 'username', label: t('hotDesking.columns.username'), sortable: true, visible: true },
    { key: 'description', label: t('hotDesking.columns.description'), sortable: false, visible: true },
    { key: 'sip_profile_name', label: t('hotDesking.columns.sipProfile'), sortable: true, visible: true },
    { key: 'enabled', label: t('hotDesking.columns.enabled'), sortable: true, visible: true },
    { key: 'created_at', label: t('hotDesking.columns.createdAt'), sortable: true, visible: false },
    { key: 'actions', label: t('common.actions'), sortable: false, visible: true },
  ], [t]);

  // Use custom hook for list management
  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handlePageChange,
    handleItemsPerPageChange,
    handleSearch,
    handleSort,
    handleColumnToggle,
    refresh,
  } = useHotDeskingList({
    initialColumns: availableColumns,
    initialSortBy: 'username',
    initialSortOrder: 'asc',
    initialLimit: 10,
    domainId: selectedDomain?.id,
  });

  const handleCreate = useCallback(() => {
    navigate('/pbx/extensions/hot-desking/new');
  }, [navigate]);

  const handleEdit = useCallback((device: HotDeskDevice) => {
    navigate(`/pbx/extensions/hot-desking/${device.uuid || device.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((device: HotDeskDevice) => {
    navigateToHotDeskDuplicate(navigate, device.uuid || device.id);
  }, [navigate]);

  const handleDeleteClick = useCallback((device: HotDeskDevice) => {
    setDeviceToDelete(device);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deviceToDelete) return;

    try {
      setIsDeleting(true);
      await deleteHotDeskById(deviceToDelete.uuid || deviceToDelete.id);
      showSuccess(t('hotDesking.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setDeviceToDelete(null);
      await refresh();
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('hotDesking.messages.deleteError');
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  }, [deviceToDelete, refresh, showError, showSuccess, t]);

  const handleViewXml = useCallback(async (device: HotDeskDevice) => {
    try {
      const xmlData = await fetchHotDeskingDeviceXml(device.uuid || device.id);
      setXmlModal({
        isOpen: true,
        device: device,
        xml: xmlData
      });
    } catch (error) {
      console.error('Error fetching XML:', error);
      const errorMessage = error instanceof Error ? error.message : t('hotDesking.messages.xmlError');
      showError(errorMessage);
    }
  }, [showError, t]);

  // Enhanced columns with custom renderers
  const enhancedColumns = useMemo(() => columns.map((col) => {
    if (col.key === 'description') {
      return {
        ...col,
        render: (_: unknown, device: HotDeskDevice) => (
          <span className="text-sm text-foreground">
            {device.description || '-'}
          </span>
        ),
      };
    }

    if (col.key === 'sip_profile_name') {
      return {
        ...col,
        render: (_: unknown, device: HotDeskDevice) => (
          <span className="text-sm text-foreground">
            {device.sip_profile_name || t('common.none')}
          </span>
        ),
      };
    }

    if (col.key === 'enabled') {
      return {
        ...col,
        render: (_: unknown, device: HotDeskDevice) => (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${device.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
              }`}
          >
            {device.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      };
    }

    if (col.key === 'created_at') {
      return {
        ...col,
        render: (_: unknown, device: HotDeskDevice) => (
          <span className="text-sm text-muted-foreground">
            {new Date(device.created_at).toLocaleDateString()}
          </span>
        ),
      };
    }

    if (col.key === 'actions') {
      return {
        ...col,
        render: (_: unknown, device: HotDeskDevice) => (
          <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.edit')} position="top">
              <button
                onClick={() => handleEdit(device)}
                className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
              >
                <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </button>
            </SimpleTooltip>
            <SimpleTooltip content={t('common.duplicate')} position="top">
              <button
                onClick={() => handleDuplicate(device)}
                className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
              >
                <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </button>
            </SimpleTooltip>

            {isDebugModeActive && (
              <SimpleTooltip content={t('hotDesking.actions.viewXml')} position="top">
                <button
                  onClick={() => handleViewXml(device)}
                  className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                  <Code className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </button>
              </SimpleTooltip>
            )}

            <SimpleTooltip content={t('common.delete')} position="top">
              <button
                onClick={() => handleDeleteClick(device)}
                className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
              >
                <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
              </button>
            </SimpleTooltip>
          </div>
        ),
      };
    }

    return col;
  }), [columns, t, handleEdit, handleDuplicate, handleDeleteClick, isDebugModeActive, handleViewXml]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            title={t('hotDesking.title')}
            data={data}
            titleIcon={<Laptop className="h-5 w-5 text-primary" />}
            loading={loading}
            columns={enhancedColumns}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onSearch={handleSearch}
            onSort={handleSort}
            onColumnToggle={handleColumnToggle}
            onAdd={handleCreate}
            searchPlaceholder={t('hotDesking.searchPlaceholder')}
            addButtonText={t('common.add')}
            emptyMessage={t('hotDesking.emptyMessage')}
            translations={{
              showing: t('common.showing'),
              to: t('common.to'),
              of: t('common.of'),
              results: t('common.results'),
              previous: t('common.previous'),
              next: t('common.next'),
              page: t('common.page'),
              deleteItem: t('common.delete'),
              areYouSureDeleteItem: t('common.areYouSureDelete'),
              delete: t('common.delete'),
              cancel: t('common.cancel'),
              items: t('common.items'),
              columns: t('common.columns'),
              noItemsFound: t('common.noItemsFound'),
              allSelected: t('common.allSelected'),
              selected: t('common.selected'),
              searchOptions: t('common.searchOptions'),
              noOptionsFound: t('common.noOptionsFound'),
              selectAll: t('common.selectAll'),
              clearAll: t('common.clearAll'),
            }}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConfirm}
            title={t('hotDesking.deleteModal.title')}
            message={t('hotDesking.deleteModal.message', { username: deviceToDelete?.username })}
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
            type="danger"
            isLoading={isDeleting}
          />

          {xmlModal.isOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border border-border rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">
                    {t('hotDesking.xmlViewer.title')} - {xmlModal.device?.username}
                  </h3>
                  <button
                    onClick={() => setXmlModal({ isOpen: false, device: null, xml: '' })}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                    {xmlModal.xml}
                  </pre>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(xmlModal.xml);
                      showSuccess(t('hotDesking.messages.xmlCopied'));
                    }}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                  >
                    {t('hotDesking.actions.copyXml')}
                  </button>
                  <button
                    onClick={() => setXmlModal({ isOpen: false, device: null, xml: '' })}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      }
      formView={null}
    />
  );
}
